import styled, { css } from 'styled-components';
import { px2rem, hoverLighten, hoverDarken } from '../utils/helpers/style.js';
import { styles } from '../constants/styles.js';
import { spacing } from '../constants/spacing.js';
import { boxStyles } from '../Box/styles.js';
import { fontFamilyStyles } from '../ContentText/styles.web.js';
import { lineHeights } from '../constants/line-heights.js';
import { TinyColor, mostReadable } from '@ctrl/tinycolor';
import { isIOS } from '../utils/helpers/isIOS.web.js';
import { boxPropExclusions } from '../Box/props.js';

var wrapper = /*#__PURE__*/styled.div.withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return !boxPropExclusions.has(prop);
  }
}).withConfig({
  displayName: "styles__wrapper",
  componentId: "sc-kz7yd-0"
})(["position:relative;overflow-x:auto;width:100%;scrollbar-width:thin;scrollbar-color:", " ", ";&::-webkit-scrollbar{background:", ";height:", ";width:", ";}&::-webkit-scrollbar-track{background:", ";box-shadow:none;}&::-webkit-scrollbar-thumb{background-color:", ";border:", " solid ", ";box-shadow:none;border-radius:", ";}", " ", ""], function (props) {
  return props.$color;
}, function (props) {
  return props.theme.table.web.background;
}, function (props) {
  return props.theme.table.web.background;
}, px2rem(8), px2rem(8), function (props) {
  return props.theme.table.web.background;
}, function (props) {
  return props.$color;
}, px2rem(2), function (props) {
  return props.theme.table.web.background;
}, function (p) {
  return p.theme.borders.radius;
}, function (p) {
  return p.$stickyHeader && css(["border-collapse:separate;"]);
}, boxStyles);
var td = /*#__PURE__*/styled.td.withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return !boxPropExclusions.has(prop);
  }
}).withConfig({
  displayName: "styles__td",
  componentId: "sc-kz7yd-1"
})(["box-sizing:border-box;line-height:", ";", " ", " ", " transition:color ", ";transition-timing-function:", ";text-align:", ";vertical-align:top;position:relative;", " ", ""], lineHeights.tight, function (props) {
  return props.$heightCssProp && css(["height:", ";"], props.$heightCssProp);
}, function (props) {
  return props.$widthCssProp && css(["width:", ";"], props.$widthCssProp);
}, function (props) {
  var _props$theme$table$we = props.theme.table.web.sizings[props.$size].cellPadding,
    top = _props$theme$table$we.top,
    right = _props$theme$table$we.right,
    bottom = _props$theme$table$we.bottom,
    left = _props$theme$table$we.left;
  var padding = "".concat(top, " ").concat(right, " ").concat(bottom, " ").concat(left);
  var height = props.theme.table.web.sizings[props.$size].rowHeight;
  return css(["height:", ";padding:", ";"], height, padding);
}, styles.transition.timing.slower, styles.transition["function"].base, function (props) {
  return props.$numeric ? 'right' : 'left';
}, function (props) {
  return props.$highlightColor ? css(["&:before{height:100%;width:", ";background:", ";content:'';left:0;position:absolute;top:0;margin-left:", ";}"], px2rem(6), props.$highlightColor, px2rem(10)) : null;
}, boxStyles);
var th = /*#__PURE__*/styled.th.withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return !boxPropExclusions.has(prop);
  }
}).withConfig({
  displayName: "styles__th",
  componentId: "sc-kz7yd-2"
})(["", " box-sizing:border-box;color:", ";line-height:", ";font-weight:500;position:relative;", " text-align:left;vertical-align:top;word-break:normal;", ""], fontFamilyStyles, function (props) {
  return props.$color;
}, lineHeights.tight, function (props) {
  var _props$theme$table$we2 = props.theme.table.web.sizings[props.$size].cellPadding,
    top = _props$theme$table$we2.top,
    right = _props$theme$table$we2.right,
    bottom = _props$theme$table$we2.bottom,
    left = _props$theme$table$we2.left;
  var padding = "".concat(top, " ").concat(right, " ").concat(bottom, " ").concat(left);
  var height = props.theme.table.web.sizings[props.$size].rowHeight;
  return css(["height:", ";padding:", ";"], height, padding);
}, boxStyles);
function getScrollShadowColor(p) {
  var _mostReadable;
  return (_mostReadable = mostReadable(p.theme.table.web.background, [p.theme.colors.text, p.theme.colors.background])) === null || _mostReadable === undefined ? undefined : _mostReadable.setAlpha(0.6).toHex8String();
}
var tr = /*#__PURE__*/styled.tr.withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return !boxPropExclusions.has(prop);
  }
}).withConfig({
  displayName: "styles__tr",
  componentId: "sc-kz7yd-3"
})(["", " transition:background ", ";transition-timing-function:", ";box-sizing:border-box;display:", ";color:", ";", " ", " ", " ", " ", ""], fontFamilyStyles, styles.transition.timing.slower, styles.transition["function"].base, function (props) {
  return props.$collapsed ? 'none' : undefined;
}, function (props) {
  return props.theme.table.web.textColor;
}, function (props) {
  return props.$clickable && css(["cursor:pointer;"]);
}, function (props) {
  return props.$selectable && css(["", ":first-child,", ":first-child{line-height:0;padding-bottom:0;}", ""], th, td, !props.$head && css(["&&&&:hover{", "{", "}}"], td, getRowHoverStyles(props.$highlighted ? 'highlighted' : 'base')(props)));
}, function (props) {
  return props.$highlighted && !props.$selected && css(["", " ", ""], getHighlightedRowStyles(props), function () {
    return props.$selectable ? css(["&:hover{", "}"], getRowHoverStyles('highlighted')(props)) : undefined;
  });
}, function (props) {
  return props.$selected && css(["&&&&{", "{", "}&:hover{", "{", "}}}"], td, getRowSelectedStyles(props), td, props.$selectable && getRowHoverStyles('selected')(props));
}, boxStyles);
var sortable = /*#__PURE__*/styled.button.withConfig({
  displayName: "styles__sortable",
  componentId: "sc-kz7yd-4"
})(["", " text-align:left;font-size:100%;padding:0;outline:none;border:none;background:none;color:inherit;cursor:pointer;font-weight:inherit;padding-right:", ";position:relative;span{position:absolute;right:0;top:50%;transform:translateY(-50%);}&:focus-visible{box-shadow:0 0 0 4px ", ";outline:2px solid ", ";outline-offset:0;}"], fontFamilyStyles, spacing.m, function (p) {
  return p.theme.tokens.focus.primary;
}, function (p) {
  return p.theme.tokens.focus.secondary;
});
var getRowHoverStyles = function getRowHoverStyles(style) {
  return function (props) {
    var _mostReadable4;
    var textColors = [props.theme.table.web.textColor, props.theme.table.web.background];
    var color = new TinyColor(props.$color);
    if (style === 'base') {
      var _mostReadable2;
      var background = new TinyColor(props.theme.table.web.background);
      var _hoverBackground = background.mix(color, 25);
      var _textColor = ((_mostReadable2 = mostReadable(_hoverBackground, textColors)) === null || _mostReadable2 === undefined ? undefined : _mostReadable2.toHexString()) || props.theme.table.web.textColor;
      return css(["color:", ";background:", ";"], _textColor, _hoverBackground.toHexString());
    }
    if (style === 'highlighted') {
      var _mostReadable3;
      var highlightColor = new TinyColor(props.$highlightColor || props.$color);
      var _luminance = highlightColor.getLuminance();
      var _hoverBackground2 = _luminance < 0.1 ? highlightColor.lighten(hoverLighten).toHexString() : highlightColor.darken(hoverDarken).toHexString();
      var _textColor2 = ((_mostReadable3 = mostReadable(_hoverBackground2, textColors)) === null || _mostReadable3 === undefined ? undefined : _mostReadable3.toHexString()) || props.theme.table.web.textColor;
      return css(["color:", ";background:", ";"], _textColor2, _hoverBackground2);
    }
    var luminance = color.getLuminance();
    var hoverBackground = luminance < 0.1 ? color.lighten(hoverLighten).toHexString() : color.darken(hoverDarken).toHexString();
    var textColor = ((_mostReadable4 = mostReadable(hoverBackground, textColors)) === null || _mostReadable4 === undefined ? undefined : _mostReadable4.toHexString()) || props.theme.table.web.textColor;
    return css(["color:", ";background:", ";"], textColor, hoverBackground);
  };
};
var getRowSelectedStyles = function getRowSelectedStyles(props) {
  var _mostReadable5;
  var background = new TinyColor(props.$color);
  var textColors = [props.theme.table.web.textColor, props.theme.table.web.background];
  var color = ((_mostReadable5 = mostReadable(background, textColors)) === null || _mostReadable5 === undefined ? undefined : _mostReadable5.toHexString()) || props.theme.table.web.textColor;
  return css(["color:", ";background:", ";"], color, props.$color);
};
var getHighlightedRowStyles = function getHighlightedRowStyles(props) {
  return getRowSelectedStyles({
    $color: props.$highlightColor || props.$color,
    theme: props.theme
  });
};
var table = /*#__PURE__*/styled.table.withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return !boxPropExclusions.has(prop);
  }
}).withConfig({
  displayName: "styles__table",
  componentId: "sc-kz7yd-5"
})(["background:", ";border-collapse:collapse;border-spacing:0;color:", ";font-size:", ";max-width:100%;text-align:left;thead ", "{th{border-bottom:", " solid ", ";background:", ";box-sizing:border-box;}}&[data-vertical-scroll='true']{thead ", "{top:-1px;opacity:1;z-index:2;clip-path:polygon(0% 0%,100% 0%,100% 120%,0% 120%);box-shadow:0 0 8px 2px ", ";", "}}&[data-horizontal-scroll='true']{thead ", " th:first-child,tbody ", " td:first-child{position:sticky;opacity:1;z-index:1;border-right:2px solid ", ";}thead ", " th:first-child{z-index:3;}}", " ", ";", " ", " ", " ", " ", " ", " ", " ", ""], function (p) {
  return p.theme.table.web.background;
}, function (p) {
  return p.theme.table.web.textColor;
}, function (props) {
  return props.$variant === 'condensed' ?
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  px2rem(props.theme.table.web.variants.condensed.fontSize) : px2rem(props.theme.table.web.fontSize);
}, tr, px2rem(2), function (p) {
  return p.theme.table.web.border;
}, function (p) {
  return p.theme.table.web.background;
}, tr, getScrollShadowColor, isIOS() && "-webkit-filter: drop-shadow(0px 0px 6px ".concat(getScrollShadowColor, ");"), tr, tr, function (p) {
  return p.theme.tokens.border.light;
}, tr, function (p) {
  return p.$stickyLeftColumn && css(["", "{th:first-child,td:first-child{position:sticky;left:0;z-index:1;}}"], tr);
}, function (p) {
  return p.$stickyHeader && css(["thead ", "{position:sticky;}thead ", " th:not(first-child){z-index:1;}"], tr, tr);
}, function (p) {
  return p.$stickyHeader && p.$stickyLeftColumn && css(["thead ", " th:first-child{left:0;z-index:2;}"], tr);
}, function (p) {
  return (p.$stickyHeader || p.$stickyLeftColumn) && css(["border-collapse:separate;"]);
}, function (props) {
  return props.$rowStyle === 'zebra' && css(["tbody ", ",tbody ", ":nth-child(odd) td{background:", ";}tbody ", ",tbody ", ":nth-child(even) td{background:", ";}thead ", "{th{border-bottom:0;}}"], tr, tr, function (p) {
    return p.theme.table.web.zebraHighlight;
  }, tr, tr, function (p) {
    return p.theme.table.web.background;
  }, tr);
}, function (props) {
  return props.$rowStyle === 'underline' && css(["tbody ", ":not(:last-child){border-bottom:", " solid ", ";}"], tr, px2rem(2), function (p) {
    return p.theme.table.web.border;
  });
}, function (props) {
  return props.$columnStyle === 'bordered' && css(["thead ", " ", ":not(:last-child){border-right:", " solid ", ";}tbody ", " ", ":not(:last-child){border-right:", " solid ", ";}"], tr, th, px2rem(2), function (p) {
    return p.theme.table.web.border;
  }, tr, td, px2rem(2), function (p) {
    return p.theme.table.web.border;
  });
}, function (props) {
  return props.$rowStyle === 'transparent' && css(["background:transparent;"]);
}, function (props) {
  return props.$clickable && css(["tbody ", ":hover{", "{", "}}"], tr, td, getRowHoverStyles('base')(props));
}, boxStyles);

export { sortable, table, td, th, tr, wrapper };
